import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $initialAccountsStore, $initialCardsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, cn } from '@shared/utils';
import { type AccountResponse, type CardResponse, type UserResponse, type UserWithPaymentInfo } from '@shared/api';
import { AccountsAndCardsPicker } from '../accountsAndCardsPicker';
import { Button, Modal } from '@shared/widgets';

interface FormData {
  accounts: number[];
  cards: CardResponse[];
}
const DEFAULT_VALUES: FormData = { accounts: [], cards: [] };

interface AddCardsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UserResponse;
  accountsItems: AccountResponse[];
  cardsItems: CardResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; accountIds: number[]; cardIds: number[] }) => Promise<unknown>;
}

export const AddCardsModal = (props: AddCardsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsStore = useUnit($initialCardsStore.store);

  const methods = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const { formState, handleSubmit, reset } = methods;

  const { t } = useTranslation();

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: FormData) => {
    await props.onSubmit({ userIds: props.user.id, accountIds: data.accounts, cardIds: data.cards.map((card) => card.cardId) });
    closeHandler();
  };

  const accounts = useMemo(() => {
    if (!profileFeatureStore.data?.accountFlows) return [];
    return AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, accountsStore.data)
      .filter((account) => account.mainAccId == null)
      .filter((account) => !props.accountsItems.map((accountItem) => accountItem.id).includes(account.id));
  }, [profileFeatureStore.data?.accountFlows, accountsStore.data, props.accountsItems]);
  const cards = useMemo(
    () => cardsStore.data.filter((card) => !props.cardsItems.map((cardItem) => cardItem.cardId).includes(card.cardId)),
    [cardsStore.data, props.cardsItems],
  );
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.addCards.title')}>
      <FormProvider {...methods}>
        <form className={cn('flex w-[640px] flex-col gap-4', 'max-md:w-[308px]')} noValidate onSubmit={handleSubmit(submitHandler)}>
          <AccountsAndCardsPicker
            accounts={accounts}
            cards={cards}
            onlyPaymentAccounts
            hint='Use the checkbox on a Payment Account to link all of its cards. Expand an account to select individual cards.'
          />
          <Button className='mt-2' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('buttons.add')}
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};
